/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { GenericTask, MicrotaskAssignmentRecord, MicrotaskInput, TaskParams, WorkerRecord } from '@karya/core';
import { GenericTaskRenderer } from '../helpers/Index';
import useAsyncData from '../utils/AsyncData';
import { models } from '../service/db/Setup';
import { useGenericTaskExecutor } from '@karya/generic-task-react';
import { useCallback, useEffect } from 'react';
import ErrorDisplay from './Error';

export interface RenderAssignmentProps {
  assignment: MicrotaskAssignmentRecord;
  taskParams: TaskParams;
  spec: GenericTask;
  worker: WorkerRecord;
  isActiveAssignment?: boolean;
  onComplete: () => void | Promise<void>;
  scrollToBottom?: () => void;
}

export function RenderAssignment(props: RenderAssignmentProps) {
  const microtask = useAsyncData(
    () => models.basic.getSingle('microtask', props.assignment.microtask_id).unwrapResult().getOrThrow(),
    {
      skip: !props.assignment.microtask_id,
      deps: [props.assignment.microtask_id],
    },
  );

  const controller = useGenericTaskExecutor({
    spec: props.spec,
    params: props.taskParams,
    mtInput: microtask.data ? (microtask.data.input as MicrotaskInput) : { data: {}, files: {} },
    // TODO: Resolve the file url map properly
    fileUrlMap: {},
    workerProfile: props.worker.profile,
    failForUnknownComponents: false,
    disableSpecValidation: false,
    persistOutputAcrossSpecChanges: false,
  });

  const handleSubmit = useCallback(async () => {
    await models.basic.upsertRecord('microtask_assignment', {
      ...props.assignment,
      status: 'COMPLETED',
      output: controller.mtaOutput,
      completed_at: new Date().toISOString(),
      report: {
        history_web: controller.history,
      },
    });
  }, [controller.history, controller.mtaOutput, props.assignment]);

  useEffect(() => {
    if (!controller.didCompleteExecution || !props.isActiveAssignment) {
      return;
    }

    handleSubmit()
      .then(props.onComplete)
      .catch((err) => {
        console.error('Error submitting assignment:', err);
      });
  }, [controller.didCompleteExecution, handleSubmit, props.isActiveAssignment, props.onComplete]);

  if (microtask.isLoading) {
    return null;
  }

  if (microtask.error) {
    return (
      <ErrorDisplay
        message="Failed to load assignment"
        debugMessage={microtask.error.message}
        navigateButton={{
          to: '/dashboard',
          label: 'Go to Dashboard',
        }}
      />
    );
  }

  return (
    <GenericTaskRenderer
      spec={props.spec}
      controller={controller}
      isActiveAssignment={props.isActiveAssignment}
      scrollToBottom={props.scrollToBottom}
    />
  );
}
